/**
 * Navigazione live: proietta la posizione GPS sul tracciato calcolato e
 * ricava quanto manca (in metri e in ombra) e se si è usciti dal percorso.
 * Pura geometria: nessuna dipendenza dal DOM, testabile in Node.
 */

import type { LatLng, XY } from './geo';
import { distXY, LocalProjection, projectPointOnSegment } from './geo';
import type { RouteResult } from './route';

export interface RouteTrack {
  proj: LocalProjection;
  /** vertici del tracciato in coordinate locali (metri) */
  pts: XY[];
  ptsLL: LatLng[];
  /** distanza cumulata a ogni vertice */
  cum: number[];
  totalM: number;
  /** ombra (0..1) di ogni intervallo pts[i] → pts[i+1] */
  shade: Float32Array;
}

/** Appiattisce i segmenti del percorso in un'unica polilinea con ombra per tratto. */
export function buildTrack(route: RouteResult): RouteTrack {
  const ptsLL: LatLng[] = [];
  const shadeList: number[] = [];
  for (const seg of route.segments) {
    for (let i = 0; i < seg.coords.length; i++) {
      const c = seg.coords[i];
      const last = ptsLL[ptsLL.length - 1];
      if (last && last.lat === c.lat && last.lng === c.lng) continue;
      if (ptsLL.length > 0) shadeList.push(seg.shade);
      ptsLL.push(c);
    }
  }
  if (ptsLL.length === 0 && route.coords.length > 0) ptsLL.push(...route.coords);
  const proj = new LocalProjection(ptsLL[0] ?? { lat: 0, lng: 0 });
  const pts = ptsLL.map((c) => proj.toXY(c));
  const cum: number[] = [0];
  for (let i = 1; i < pts.length; i++) cum.push(cum[i - 1] + distXY(pts[i - 1], pts[i]));
  return {
    proj,
    pts,
    ptsLL,
    cum,
    totalM: cum[cum.length - 1] ?? 0,
    shade: Float32Array.from(
      shadeList.length === pts.length - 1
        ? shadeList
        : new Array(Math.max(0, pts.length - 1)).fill(0),
    ),
  };
}

export const OFF_ROUTE_M = 35;

export interface NavFix {
  /** metri percorsi lungo il tracciato (proiezione della posizione) */
  alongM: number;
  remainingM: number;
  /** distanza dalla posizione al punto più vicino del tracciato */
  distToRouteM: number;
  offRoute: boolean;
  /** 0..1 */
  progress: number;
  /** ombra media, pesata sulla lunghezza, del tratto ancora da percorrere */
  remainingShadeFrac: number;
  /** punto del tracciato più vicino alla posizione */
  snapped: LatLng;
  segmentIndex: number;
}

/**
 * Localizza `pos` sul tracciato. La soglia di "fuori percorso" cresce con
 * l'imprecisione del GPS: con 50 m di accuratezza non ha senso allarmarsi a 35.
 */
export function locateOnTrack(track: RouteTrack, pos: LatLng, accuracyM = 0): NavFix {
  const q = track.proj.toXY(pos);
  let bestD2 = Infinity;
  let bestIdx = 0;
  let bestT = 0;
  let bestPt: XY = track.pts[0] ?? q;
  for (let i = 0; i + 1 < track.pts.length; i++) {
    const r = projectPointOnSegment(q, track.pts[i], track.pts[i + 1]);
    if (r.d2 < bestD2) {
      bestD2 = r.d2;
      bestIdx = i;
      bestT = r.t;
      bestPt = r.point;
    }
  }
  const segLen = track.pts.length > 1 ? track.cum[bestIdx + 1] - track.cum[bestIdx] : 0;
  const alongM = (track.cum[bestIdx] ?? 0) + bestT * segLen;
  const remainingM = Math.max(0, track.totalM - alongM);

  // ombra rimanente: resto del segmento corrente + tutti i successivi
  let shadeWeighted = (1 - bestT) * segLen * (track.shade[bestIdx] ?? 0);
  for (let j = bestIdx + 1; j + 1 < track.pts.length; j++) {
    shadeWeighted += (track.cum[j + 1] - track.cum[j]) * (track.shade[j] ?? 0);
  }
  const distToRouteM = Math.sqrt(bestD2);
  return {
    alongM,
    remainingM,
    distToRouteM,
    offRoute: distToRouteM > Math.max(OFF_ROUTE_M, accuracyM * 1.5),
    progress: track.totalM > 0 ? alongM / track.totalM : 1,
    remainingShadeFrac: remainingM > 0 ? shadeWeighted / remainingM : 0,
    snapped: track.proj.toLatLng(bestPt),
    segmentIndex: bestIdx,
  };
}
